import { useState } from "react";
import type { NightlyItem } from "../../../shared/home";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useMembers } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { useNightly, useNightlyActions } from "./api";
import "./home.css";

interface NightlyConfig {
  title: string;
}

/** The before-bed checklist: doors, garage, stove, backpacks. Ticks reset every evening. */
function Nightly({ config, editing, fullscreen, openFullscreen }: WidgetProps<NightlyConfig>) {
  const { isAdmin } = useHousehold();
  useNow("minute");
  const q = useNightly();
  const members = useMembers().data ?? [];
  const byId = new Map(members.map((m) => [m.id, m]));
  const actions = useNightlyActions();
  const { toast } = useAppUi();
  const state = dataStateOf(q, 15 * 60_000);
  const [editList, setEditList] = useState(false);
  const data = q.data;
  const checked = new Map((data?.checks ?? []).map((c) => [c.itemId, c]));
  const done = data ? data.items.filter((i) => checked.has(i.id)).length : 0;
  const allSet = Boolean(data && data.items.length > 0 && done === data.items.length);

  const toggle = (item: NightlyItem) => {
    if (!data) return;
    actions.check.mutate({ itemId: item.id, checked: !checked.has(item.id), date: data.date }, { onError: (e) => toast(`Couldn’t update “${item.label}”: ${(e as Error).message}`) });
  };

  return (
    <div className={`home-w nightly ${allSet ? "is-all-set" : ""}`}>
      <WidgetHeader
        title={config.title || "Before bed"}
        meta={data && data.items.length ? `${done} of ${data.items.length}` : undefined}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          isAdmin ? (
            <button className="icon-btn" onClick={() => setEditList(true)} aria-label="Edit the checklist" disabled={editing}>
              <Icon name="edit" size="1.1rem" />
            </button>
          ) : undefined
        }
      />
      {!data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="moonCheck" title="Checklist unavailable" detail={q.error?.message} />
      ) : data.items.length === 0 ? (
        <EmptyState icon="moonCheck" title="No bedtime checklist" detail={isAdmin ? "Add the things to check every night." : "An adult can set one up."} />
      ) : (
        <>
          {allSet && (
            <div className="nightly-done">
              <Icon name="moonCheck" size="1.4rem" /> All set for the night
            </div>
          )}
          <ul className="nightly-list">
            {data.items.map((item) => {
              const c = checked.get(item.id);
              const who = c?.checkedBy ? byId.get(c.checkedBy) : undefined;
              return (
                <li key={item.id}>
                  <button className={`nightly-item ${c ? "on" : ""}`} onClick={() => toggle(item)} disabled={editing} aria-pressed={Boolean(c)}>
                    <span className="nightly-icon" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="nightly-label">{item.label}</span>
                    {who && <Avatar member={who} size="1.6rem" />}
                    <span className="nightly-box">{c && <Icon name="check" size="1rem" />}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {editList && data && <ChecklistEditor items={data.items} onClose={() => setEditList(false)} />}
    </div>
  );
}

function ChecklistEditor({ items, onClose }: { items: NightlyItem[]; onClose: () => void }) {
  const actions = useNightlyActions();
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("");
  const [error, setError] = useState<string | null>(null);
  const onError = (e: Error) => setError(e.message);
  const add = () => {
    if (!label.trim()) return;
    actions.add.mutate({ label, icon }, { onSuccess: () => (setLabel(""), setIcon("")), onError });
  };

  return (
    <Sheet
      title="Bedtime checklist"
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="form">
        <p className="muted">Everyone sees the same list on every screen. Ticks clear each evening.</p>
        <ul className="nightly-edit">
          {items.map((i) => (
            <li key={i.id}>
              <input className="input nightly-edit-icon" defaultValue={i.icon} maxLength={4} aria-label="Icon" onBlur={(e) => e.target.value !== i.icon && actions.update.mutate({ id: i.id, label: i.label, icon: e.target.value }, { onError })} />
              <input className="input" defaultValue={i.label} maxLength={60} aria-label="Item" onBlur={(e) => e.target.value.trim() && e.target.value !== i.label && actions.update.mutate({ id: i.id, label: e.target.value, icon: i.icon }, { onError })} />
              <button className="icon-btn" onClick={() => actions.remove.mutate(i.id, { onError })} aria-label={`Remove ${i.label}`}>
                <Icon name="trash" size="1.1rem" />
              </button>
            </li>
          ))}
        </ul>
        <div className="nightly-edit nightly-add">
          <input className="input nightly-edit-icon" value={icon} onChange={(e) => setIcon(e.target.value)} maxLength={4} placeholder="✓" aria-label="Icon for the new item" />
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} maxLength={60} placeholder="e.g. Dog let out" aria-label="New item" />
          <button className="btn btn-primary" onClick={add} disabled={!label.trim() || actions.add.isPending}>
            Add
          </button>
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
    </Sheet>
  );
}

export const nightlyWidget: WidgetDefinition<NightlyConfig> = {
  type: "nightly",
  version: 1,
  name: "Before bed",
  description: "The nightly checklist: doors locked, garage closed, stove off, backpacks ready. Clears every evening.",
  icon: "moonCheck",
  category: "household",
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 380,
  defaults: { title: "" },
  settings: [{ key: "title", label: "Title", type: "text", maxLength: 40, placeholder: "Before bed" }],
  supportsFullscreen: true,
  Component: Nightly,
};
